import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Shield as ShieldIcon, Warning as WarningIcon } from '@mui/icons-material';

import {
  msHastaGrace,
  UMBRAL_MODAL_BLOQUEANTE_MS,
  useMfaStatusStore,
} from '@/store/auth/mfa-status.store';
import { fetchDosFactorEstado } from '@/shared/actions/auth/dos-factor.actions';
import { useAuthStore } from '@/store/auth/auth.store';

const PERFIL_2FA_PATH = '/perfil?setup=2fa';

const formatearTiempoRestante = (ms: number): string => {
  if (!isFinite(ms) || ms <= 0) return 'vencido';
  const minutos = Math.floor(ms / 60000);
  const horas = Math.floor(minutos / 60);
  const dias = Math.floor(horas / 24);
  if (dias >= 1) {
    const horasRestantes = horas - dias * 24;
    return horasRestantes > 0
      ? `${dias} día(s) y ${horasRestantes} hora(s)`
      : `${dias} día(s)`;
  }
  if (horas >= 1) {
    const minutosRestantes = minutos - horas * 60;
    return `${horas} hora(s) y ${minutosRestantes} min`;
  }
  return `${minutos} min`;
};

export const MfaSetupGate = () => {
  const setupPendiente = useMfaStatusStore((s) => s.setupPendiente);
  const graceUntil = useMfaStatusStore((s) => s.graceUntil);
  const redirected = useMfaStatusStore((s) => s.redirected);
  const setRedirected = useMfaStatusStore((s) => s.setRedirected);
  const isAuth = useAuthStore((s) => s.isAuth);

  const navigate = useNavigate();
  const location = useLocation();

  // forzar re-render cada minuto para actualizar el contador y cruzar el umbral
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!setupPendiente) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [setupPendiente]);

  // Polling cada 60s al estado real del backend para sincronizar cambios admin
  // (cambio de grace, obligatorio quitado, etc). Solo mientras hay sesión activa.
  useEffect(() => {
    if (!isAuth) return;
    let cancelado = false;
    const sincronizar = async () => {
      try {
        const estado = await fetchDosFactorEstado();
        if (cancelado) return;
        const pendienteBackend = Boolean(estado?.mfa_setup_pendiente);
        const graceBackend = estado?.mfa_grace_until ?? null;
        const store = useMfaStatusStore.getState();
        if (
          store.setupPendiente !== pendienteBackend ||
          store.graceUntil !== graceBackend
        ) {
          // Mantener el flag redirected si seguía pendiente — solo se resetea
          // en login real. Aquí actualizamos sin resetearlo.
          useMfaStatusStore.setState({
            setupPendiente: pendienteBackend,
            graceUntil: graceBackend,
          });
        }
      } catch {
        /* silencioso: si el endpoint falla no rompemos la app */
      }
    };
    sincronizar();
    const id = window.setInterval(sincronizar, 60_000);
    return () => {
      cancelado = true;
      window.clearInterval(id);
    };
  }, [isAuth]);

  // Redirect post-login al perfil → tab Doble Factor (modo B). Solo una vez por sesión.
  useEffect(() => {
    if (!setupPendiente) return;
    if (redirected) return;
    if (location.pathname === '/perfil' || location.pathname.startsWith('/perfil/')) {
      setRedirected(true);
      return;
    }
    navigate(PERFIL_2FA_PATH);
    setRedirected(true);
  }, [setupPendiente, redirected, location.pathname, navigate, setRedirected]);

  if (!setupPendiente) return null;

  const ms = msHastaGrace(graceUntil);
  const tiempoTexto = formatearTiempoRestante(ms);
  const yaVencido = isFinite(ms) && ms <= 0;
  const modoBloqueante = isFinite(ms) && ms < UMBRAL_MODAL_BLOQUEANTE_MS;

  // Modo C: modal bloqueante cuando queda menos de 1 día (o ya venció).
  // Si el usuario YA está en /perfil/* lo dejamos pasar al banner —
  // de otro modo el modal taparía la pestaña Doble Factor y no podría configurar.
  const enPerfilDoble =
    location.pathname === '/perfil' ||
    location.pathname.startsWith('/perfil/');
  if (modoBloqueante && !enPerfilDoble) {
    return (
      <Dialog
        open
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        onClose={() => {
          /* bloqueado intencionalmente */
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon color="error" />
            <Typography variant="h6">Configura tu doble factor</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body1">
              Tu cuenta requiere doble factor de autenticación.{' '}
              {yaVencido
                ? 'Tu periodo de gracia venció y debes configurarlo ahora para continuar.'
                : `Te queda menos de ${tiempoTexto} para configurarlo.`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configura una app autenticadora (Google / Microsoft / Duo) o el
              método por correo. Sin esto, no podrás continuar usando el sistema.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShieldIcon />}
            onClick={() => navigate(PERFIL_2FA_PATH)}
          >
            Configurar ahora
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Modo B: banner persistente dentro del flow del LayoutContainer
  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="warning"
        icon={<ShieldIcon />}
        action={
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={() => navigate(PERFIL_2FA_PATH)}
          >
            Configurar ahora
          </Button>
        }
      >
        <AlertTitle>Configura tu doble factor</AlertTitle>
        Tu cuenta requiere 2FA. Te quedan <strong>{tiempoTexto}</strong> antes
        de que sea obligatorio para iniciar sesión.
      </Alert>
    </Box>
  );
};

export default MfaSetupGate;
